import { FC, useMemo } from 'react';
import cls from './subscriptions-table.module.scss';
import {
  DomainStatus,
  SubscriptionEntry,
  SubscriptionState,
} from 'store/api/apiTypes';
import { useTranslation } from 'hooks/api/useTranslation';
import { formatDate } from 'shared/helpers/format-date';
import { Table } from 'shared/ui/table/table';
import { TableColumn } from 'shared/ui/table/table.types';
import { SubscriptionActionsCell } from './subscription-actions-cell/subscription-actions-cell';
import {
  BASE_PLAN,
  planOf,
} from './subscription-actions-cell/subscription-actions-cell.type';
import { useCachedUserType } from 'hooks';

interface SubscriptionsTableProps {
  licenses: SubscriptionEntry[];
  searchQuery?: string;
  isLoading?: boolean;
  onNavigateToWebsites: () => void;
}

export const SubscriptionsTable: FC<SubscriptionsTableProps> = ({
  licenses,
  isLoading,
  searchQuery = '',
  onNavigateToWebsites,
}) => {
  const { t } = useTranslation();
  const { isDirect } = useCachedUserType();

  const filteredSubscriptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? licenses.filter(
          l =>
            l.license?.id?.toString()?.toLowerCase().includes(q) ||
            l.domain?.name?.toLowerCase().includes(q)
        )
      : licenses;

    return [...filtered].sort((a, b) => {
      if (planOf(a) === BASE_PLAN && planOf(b) !== BASE_PLAN) return -1;
      if (planOf(a) !== BASE_PLAN && planOf(b) === BASE_PLAN) return 1;
      return 0;
    });
  }, [licenses, searchQuery]);

  const isCancelling = (license: SubscriptionEntry) =>
    isDirect && license.license.state === SubscriptionState.CANCELING;

  const columns: TableColumn<SubscriptionEntry>[] = [
    {
      id: 'website',
      header: t('website'),
      width: '34%',
      renderCell: l => (
        <div className={cls.websiteCell}>
          <span className={cls.websiteName}>
            {l.domain?.name || t('notAssigned')}
          </span>
          {isDirect && (
            <span className={cls.subscriptionType}>
              {planOf(l) === BASE_PLAN
                ? t('rivaLicense')
                : t('additionalRivaLicense')}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'license',
      header: t('license'),
      width: '12%',
      renderCell: l => l.license.id,
    },
    {
      id: 'status',
      header: t('status'),
      width: '12%',
      renderCell: l =>
        isCancelling(l)
          ? t('cancelling')
          : l.domain?.state === DomainStatus.EXPIRED
            ? t('expired')
            : l.domain?.name
              ? t('assigned')
              : t('unassigned'),
    },
    {
      id: 'nextBilling',
      header: t('nextBillingDate'),
      width: '20%',
      renderCell: l =>
        isCancelling(l)
          ? '—'
          : formatDate(
              l.license.next_billing_date
                ? new Date(l.license.next_billing_date)
                : null,
              'long',
              '—'
            ),
    },
    {
      id: 'actions',
      header: '',
      width: '22%',
      align: 'right',
      renderCell: l => (
        <SubscriptionActionsCell
          license={l}
          allSubscriptions={licenses}
          onNavigateToWebsites={onNavigateToWebsites}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={filteredSubscriptions}
      getRowKey={license => license.license.id}
      isLoading={isLoading}
      emptyState={
        searchQuery.trim() ? t('licensesNoSearchResults') : t('licensesNoData')
      }
    />
  );
};
